import React, { useState, useEffect } from 'react';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import { Separator } from 'app/components/ui/separator';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import { Textarea } from 'app/components/ui/textarea';
import { Badge } from 'app/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'app/components/ui/tabs';
import {
  Send,
  Eye,
  EyeOff,
  TestTube,
  Send as SendIcon,
  CheckCircle,
  AlertCircle,
  Loader2,
  Settings,
  Activity,
  FileText,
} from 'lucide-react';
import { useToast } from 'app/components/ui/use-toast';

interface MailgunConfigData {
  id?: string;
  sender_name: string;
  sender_email: string;
  api_key: string;
  domain: string;
  webhook_signing_key: string;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

interface TestEmailData {
  testEmail: string;
  subject: string;
  message: string;
}

interface MailgunPopupProps {
  isOpen: boolean;
  onClose: () => void;
  config?: MailgunConfigData | null;
  onSave: (config: MailgunConfigData) => Promise<void>;
  onGetLogs?: () => Promise<any>;
}

type PopupTab = 'edit' | 'logs';

const MailgunPopup: React.FC<MailgunPopupProps> = ({
  isOpen,
  onClose,
  config,
  onSave,
  onGetLogs,
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<PopupTab>('edit');
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);

  // Edit form state
  const [formData, setFormData] = useState<MailgunConfigData>({
    sender_name: '',
    sender_email: '',
    api_key: '',
    domain: '',
    webhook_signing_key: '',
    is_active: 0,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookKey, setShowWebhookKey] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        id: config.id,
        sender_name: config.sender_name || '',
        sender_email: config.sender_email || '',
        api_key: config.api_key || '',
        domain: config.domain || '',
        webhook_signing_key: config.webhook_signing_key || '',
        is_active: config.is_active || 0,
        created_at: config.created_at,
        updated_at: config.updated_at,
      });
    }
  }, [config]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (
        !formData.sender_name ||
        !formData.sender_email ||
        !formData.api_key ||
        !formData.domain ||
        !formData.webhook_signing_key
      ) {
        throw new Error('Please fill in all required fields');
      }

      await onSave(formData);
      toast({
        title: config
          ? 'Mailgun Configuration Updated'
          : 'Mailgun Configuration Activated',
        description: config
          ? 'Configuration updated successfully.'
          : 'Configuration activated successfully.',
      });
    } catch (error) {
      console.error('Error saving Mailgun config:', error);
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to save configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetLogs = async () => {
    if (!onGetLogs) return;

    setLogsLoading(true);
    try {
      const result = await onGetLogs();
      setLogs(result?.data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch logs',
        variant: 'destructive',
      });
    } finally {
      setLogsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      sender_name: '',
      sender_email: '',
      api_key: '',
      domain: '',
      webhook_signing_key: '',
      is_active: 0,
    });
    setLogs([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl">
        <DialogHeader className="bg-white">
          <DialogTitle className="flex items-center gap-2 border-b border-gray-200 pb-2">
            <div>
              <img
                src="/images/mailgun.svg"
                alt="Mailgun"
                className="w-24 h-auto"
              />
            </div>
            <div className="text-xl font-semibold text-gray-800">
              Mailgun Configuration
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-6 p-5">
          {/* Basic Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Configuration</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="senderName">Sender Name *</Label>
                <Input
                  id="senderName"
                  placeholder="Filewale"
                  value={formData.sender_name}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      sender_name: e.target.value,
                    })
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senderEmail">Sender Email *</Label>
                <Input
                  id="senderEmail"
                  type="email"
                  placeholder="contact@filewale.com"
                  value={formData.sender_email}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      sender_email: e.target.value,
                    })
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="domain">Domain *</Label>
                <Input
                  id="domain"
                  placeholder="filewale.com"
                  value={formData.domain}
                  onChange={e =>
                    setFormData({ ...formData, domain: e.target.value })
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Your verified domain in Mailgun
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* API Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">API Configuration</h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key *</Label>
                <div className="relative">
                  <Input
                    id="apiKey"
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="0931df0f757171f5ebd7cecd54c46781-03fd4b1a-53152843"
                    value={formData.api_key}
                    onChange={e =>
                      setFormData({ ...formData, api_key: e.target.value })
                    }
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Get your API key from your Mailgun dashboard
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhookSigningKey">Webhook Signing Key *</Label>
                <div className="relative">
                  <Input
                    id="webhookSigningKey"
                    type={showWebhookKey ? 'text' : 'password'}
                    placeholder="57973389d333a33cb633a639c12b65c5"
                    value={formData.webhook_signing_key}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        webhook_signing_key: e.target.value,
                      })
                    }
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowWebhookKey(!showWebhookKey)}
                  >
                    {showWebhookKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Required for Mailgun webhook verification
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Status */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={!!formData.is_active}
                onCheckedChange={checked =>
                  setFormData({ ...formData, is_active: checked ? 1 : 0 })
                }
              />
              <Label htmlFor="isActive">Active Provider</Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Only active providers will be used for sending emails
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? 'Saving...'
                : config
                ? 'Update Configuration'
                : 'Activate Configuration'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MailgunPopup;
